import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../hooks/useAuth';
import InstitutionProfile from './InstitutionProfile';
import { useToast } from '../hooks/useToast';
import { UserRole, CommunicationLanguage } from '../types/auth';
import { Study } from '../types/member';
import type { IndividualMemberDto } from '../types/member';
import { memberService } from '../services/members';
import { changeRequestService } from '../services/changeRequests';
import { ChangeRequest, ChangeRequestStatus } from '../types/changeRequest';
import { committeeService } from '../services/committees';
import { InternationalCommittee } from '../types/committee';
import Modal from '../components/common/Modal';
import LoadingSpinner from '../components/common/LoadingSpinner';
import MemberProfileSectionEditor, {
  type ProfileSectionId,
} from '../components/profile/MemberProfileSectionEditor';
import { Button } from '../design-system/components/Button';
import { Card } from '../design-system/components/Card';
import { Heading, Text } from '../design-system/components/Typography';
import { spacing } from '../design-system/tokens/spacing';
import { typography } from '../design-system/tokens/typography';
import { breakpoints } from '../design-system/tokens/breakpoints';
import * as validation from '../utils/validation';
import {
  getApiErrorMessage,
  getProfileFieldErrorsFromApi,
  getValidationErrorMessage,
} from '../utils/apiErrors';
import {
  COMMITTEES_INFO_LIST,
  MemberFormData,
  createEmptyMemberForm,
  memberDtoToFormData,
  buildMemberUpdateDto,
} from '../utils/memberForm';
import { validateStudiesList } from '../utils/studyValidation';
import { applyPendingMemberChanges } from '../utils/pendingMemberEdits';
import { enumMatches } from '../utils/enumNormalize';

const EditModeShell = styled.div`
  min-height: calc(100vh - 120px);
  background: ${props => props.theme.colors.gradientBackground};
  padding: ${spacing[8]} ${spacing[4]};

  ${breakpoints.down.md} {
    padding: ${spacing[5]} ${spacing[3]};
  }
`;

const CardWithPadding = styled(Card)`
  padding: ${spacing[8]};
  max-width: 1000px;
  margin: 0 auto;

  ${breakpoints.down.md} {
    padding: ${spacing[5]};
  }
`;

const PageHeader = styled.div`
  margin-bottom: ${spacing[5]};
`;

const HeadingWithMargin = styled(Heading)`
  margin-bottom: ${spacing[2]};
`;

const BackLink = styled(Link)`
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  font-family: ${typography.fontFamily.primary};
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[4]};
  transition: color 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    color: ${props => props.theme.colors.primaryDark};
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const TextWithMargin = styled(Text)`
  margin-bottom: ${spacing[4]};
  display: block;
`;

const ExternalLink = styled.a`
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${spacing[4]};
  display: block;
`;

const CommitteesList = styled.ul`
  margin: ${spacing[4]} 0 0 0;
  padding-left: ${spacing[6]};
  color: ${props => props.theme.colors.text};
  font-size: ${typography.fontSize.sm};
  line-height: 1.6;
`;

const ProfileEdit: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated, refreshUser } = useAuth();
  const { success: showSuccess, error: showError } = useToast();

  const getCurrentCommunicationLanguage = (): CommunicationLanguage => {
    const lang = i18n.language.toLowerCase();
    if (lang.startsWith('el') || lang.startsWith('gr')) return CommunicationLanguage.Greek;
    if (lang.startsWith('fr')) return CommunicationLanguage.French;
    if (lang.startsWith('es')) return CommunicationLanguage.Spanish;
    return CommunicationLanguage.English;
  };

  const [loading, setLoading] = useState(true);
  const [member, setMember] = useState<IndividualMemberDto | null>(null);
  const [committees, setCommittees] = useState<InternationalCommittee[]>([]);
  const [formData, setFormData] = useState<MemberFormData>(() =>
    createEmptyMemberForm(getCurrentCommunicationLanguage())
  );
  const [approvedFormData, setApprovedFormData] = useState<MemberFormData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [committeesModalOpen, setCommitteesModalOpen] = useState(false);
  const [committeesLoadError, setCommitteesLoadError] = useState<string | null>(null);
  const [pendingChanges, setPendingChanges] = useState<ChangeRequest[]>([]);

  const loadCommittees = async () => {
    setCommitteesLoadError(null);
    try {
      const data = await committeeService.getAllCommittees();
      setCommittees(data.filter(c => c.isActive));
    } catch (err) {
      const message = err instanceof Error ? err.message : t('register.committees.loadError');
      setCommitteesLoadError(message);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (user?.role === UserRole.InstitutionMember) {
      return;
    }
    if (user?.role !== UserRole.IndividualMember) {
      navigate('/');
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [memberData, committeesData, changeRequests] = await Promise.all([
          memberService.getMyMember(),
          committeeService.getAllCommittees(),
          changeRequestService.getMyChangeRequests().catch(() => [] as ChangeRequest[]),
        ]);
        if (cancelled) return;
        const activeCommittees = (committeesData ?? []).filter(c => c.isActive);
        setCommittees(activeCommittees);
        if (!memberData) {
          setMember(null);
          setLoading(false);
          return;
        }
        const pending = (changeRequests ?? []).filter(cr =>
          enumMatches(ChangeRequestStatus, cr.status, ChangeRequestStatus.Pending)
        );
        setPendingChanges(pending);
        setMember(memberData);
        const approved = memberDtoToFormData(
          memberData,
          activeCommittees,
          getCurrentCommunicationLanguage()
        );
        setApprovedFormData(approved);
        setFormData(applyPendingMemberChanges(approved, pending));
      } catch (err) {
        if (!cancelled) {
          showError(err instanceof Error ? err.message : t('profile.loadError'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.role, navigate]);

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      communicationLanguage: getCurrentCommunicationLanguage(),
    }));
  }, [i18n.language]);

  const validateField = (fieldName: string, value: string): string | undefined => {
    switch (fieldName) {
      case 'dateOfBirth':
        return validation.validateDate(value, false).error;
      default:
        return undefined;
    }
  };

  const handleFieldBlur = (fieldName: string) => {
    const value = (formData as unknown as Record<string, unknown>)[fieldName];
    if (typeof value === 'string') {
      const rawError = validateField(fieldName, value);
      if (rawError) {
        setFieldErrors(prev => ({
          ...prev,
          [fieldName]: getValidationErrorMessage(rawError, t),
        }));
      }
    }
  };

  const validateSection = (sectionId: ProfileSectionId): boolean => {
    setError(null);
    const errors: Record<string, string> = {};
    let isValid = true;
    const { working: isWorking, studying: isStudying, retired: isRetired } =
      formData.professionalActivities;

    if (sectionId === 'personal') {
      const hasFirst = !!(formData.firstNameEn?.trim() || formData.firstNameGr?.trim());
      if (!hasFirst) {
        errors.firstName = t('register.firstNameRequired');
        isValid = false;
      }
      const lastNameErrorKey = validation.bilingualLastNameErrorKey(
        formData.lastNameEn,
        formData.lastNameGr
      );
      if (lastNameErrorKey) {
        errors.lastName = t(lastNameErrorKey);
        isValid = false;
      }
      const dobPartsResult = validation.validateOptionalDateParts(
        {
          day: formData.dateOfBirthDay,
          month: formData.dateOfBirthMonth,
          year: formData.dateOfBirthYear,
        },
        formData.dateOfBirth
      );
      if (!dobPartsResult.isValid) {
        errors.dateOfBirth = getValidationErrorMessage(
          dobPartsResult.error || 'Please enter a valid date',
          t
        );
        isValid = false;
      }
      const phoneResult = validation.validatePhone(formData.phone ?? '', false);
      if (!phoneResult.isValid) {
        errors.phone = getValidationErrorMessage(
          phoneResult.error || 'validation.phoneInvalid',
          t
        );
        isValid = false;
      }
      const mobileResult = validation.validatePhone(formData.mobile ?? '', false);
      if (!mobileResult.isValid) {
        errors.mobile = getValidationErrorMessage(
          mobileResult.error || 'validation.phoneInvalid',
          t
        );
        isValid = false;
      }
      const website = formData.website?.trim() ?? '';
      if (website) {
        const urlResult = validation.validateUrl(website, false);
        if (!urlResult.isValid) {
          errors.website = getValidationErrorMessage(
            urlResult.error || 'Please enter a valid URL',
            t
          );
          isValid = false;
        }
      }
      if (!isValid) {
        setError(Object.values(errors)[0] || t('register.formErrorsBelow'));
      }
    }

    if (sectionId === 'activities') {
      if (!(isWorking || isStudying || isRetired)) {
        setError(t('register.professionalSelectAtLeastOne'));
        isValid = false;
      }
    }

    if (sectionId === 'work') {
      if (isWorking) {
        const hasInstitution = !!(
          formData.professionalInstitutionNameEn?.trim() ||
          formData.professionalInstitutionNameGr?.trim()
        );
        if (!hasInstitution) {
          errors.professionalInstitution = t('register.professionalInstitutionRequired');
          isValid = false;
        }
      }
      const profEmail = formData.professionalEmail?.trim() ?? '';
      if (profEmail) {
        const emailResult = validation.validateEmail(profEmail);
        if (!emailResult.isValid) {
          errors.professionalEmail = getValidationErrorMessage(
            emailResult.error || 'Please enter a valid email address',
            t
          );
          isValid = false;
        }
      }
      const profPhoneResult = validation.validatePhone(formData.professionalPhone ?? '', false);
      if (!profPhoneResult.isValid) {
        errors.professionalPhone = getValidationErrorMessage(
          profPhoneResult.error || 'validation.phoneInvalid',
          t
        );
        isValid = false;
      }
      if (!isValid) {
        const firstFieldError = Object.values(errors)[0];
        setError(firstFieldError || t('register.formErrorsBelow'));
      }
    }

    if (sectionId === 'studies') {
      const studyErrors = validateStudiesList(formData.studies, t, {
        requireAtLeastOne: isStudying,
      });
      Object.assign(errors, studyErrors);
      if (Object.keys(studyErrors).length > 0) {
        isValid = false;
        const chronologyError =
          Object.entries(studyErrors).find(([key]) =>
            key.includes('completionYear') || key.includes('startYear') || key.includes('duration')
          )?.[1] || studyErrors.studies;
        setError(chronologyError || t('register.studies.yearsOutOfOrder'));
      }
    }

    if (sectionId === 'committees') {
      if (!formData.mainCommittee) {
        setError(t('register.committees.mainRequired'));
        isValid = false;
      } else if (formData.additionalCommittees.length > 3) {
        setError(t('register.committees.maxReached'));
        isValid = false;
      } else {
        setError(null);
      }
    }

    setFieldErrors(errors);
    return isValid;
  };

  const handleSaveSection = async (sectionId: ProfileSectionId): Promise<number> => {
    if (!member?.id) {
      throw new Error(t('profile.notFound'));
    }
    setError(null);
    try {
      const dto = buildMemberUpdateDto(formData, member, committees, sectionId);
      const created = await memberService.updateMyMember(dto);
      await refreshUser();
      const [fresh, changeRequests] = await Promise.all([
        memberService.getMyMember(),
        changeRequestService.getMyChangeRequests().catch(() => [] as ChangeRequest[]),
      ]);
      const pending = (changeRequests ?? []).filter(cr =>
        enumMatches(ChangeRequestStatus, cr.status, ChangeRequestStatus.Pending)
      );
      setPendingChanges(pending);
      if (fresh) {
        setMember(fresh);
        const approved = memberDtoToFormData(
          fresh,
          committees,
          getCurrentCommunicationLanguage()
        );
        setApprovedFormData(approved);
        setFormData(applyPendingMemberChanges(approved, pending));
      }
      const createdCount = created?.length ?? 0;
      if (createdCount === 0) {
        const message = t('profile.noChangesSubmitted', {
          defaultValue: 'No new change was submitted. The approved profile is unchanged.',
        });
        setError(message);
        showError(message);
      } else {
        showSuccess(
          createdCount > 1
            ? t('profile.changeRequestSubmittedCount', { count: createdCount })
            : t('profile.changeRequestSubmitted')
        );
      }
      return createdCount;
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      const fieldFromApi = getProfileFieldErrorsFromApi(message, t);
      if (Object.keys(fieldFromApi).length > 0) {
        setFieldErrors(prev => ({ ...prev, ...fieldFromApi }));
      }
      const friendlyMessage = getApiErrorMessage(message, t, { profile: true });
      setError(friendlyMessage);
      showError(friendlyMessage);
      throw new Error(friendlyMessage);
    }
  };

  if (user?.role === UserRole.InstitutionMember) {
    return <InstitutionProfile startInEdit />;
  }

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (!member) {
    return (
      <EditModeShell>
        <CardWithPadding variant="elevated">
          <Text color="error">{t('profile.notFound')}</Text>
          <Button variant="outline" onClick={() => navigate('/profile')}>
            {t('common.back')}
          </Button>
        </CardWithPadding>
      </EditModeShell>
    );
  }

  return (
    <EditModeShell>
      <CardWithPadding variant="elevated" accentBorder="top">
        <PageHeader>
          <HeadingWithMargin variant="h1" color="primary">
            {t('profile.editProfile')}
          </HeadingWithMargin>
          <Text color="secondary" size="base">
            {t('profile.editSubtitle')}
          </Text>
        </PageHeader>
        <BackLink to="/profile">
          <ArrowLeftIcon aria-hidden />
          {t('common.back')}
        </BackLink>
        <MemberProfileSectionEditor
          formData={formData}
          setFormData={setFormData}
          committees={committees}
          committeesLoadError={committeesLoadError}
          onRetryCommittees={loadCommittees}
          onOpenCommitteesInfo={() => setCommitteesModalOpen(true)}
          fieldErrors={fieldErrors}
          onFieldBlur={handleFieldBlur}
          t={t}
          validateSection={validateSection}
          onSaveSection={handleSaveSection}
          sectionError={error}
          onClearSectionError={() => {
            setError(null);
            setFieldErrors({});
          }}
          approvalNotice={String(t('profile.changesRequireApproval'))}
          pendingChanges={pendingChanges}
          approvedFormData={approvedFormData}
        />
        <Modal
          isOpen={committeesModalOpen}
          onClose={() => setCommitteesModalOpen(false)}
          title={t('register.aboutCommittees')}
          maxWidth="560px"
        >
          <TextWithMargin size="sm" color="secondary">
            {t('register.committeesInfoIntro')}
          </TextWithMargin>
          <ExternalLink href={t('register.committeesLink')} target="_blank" rel="noopener noreferrer">
            {t('register.committeesLink')}
          </ExternalLink>
          <CommitteesList>
            {COMMITTEES_INFO_LIST.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </CommitteesList>
        </Modal>
      </CardWithPadding>
    </EditModeShell>
  );
};

export default ProfileEdit;
